import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { Controller, Resolver, useForm } from "react-hook-form";
import { IBeacon } from "../../../shared/interfaces/beacon/IBeacon";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import BreadcrumbItem from "../../../shared/components/BreadcrumbItem";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { handleError } from "../../../shared/functions/handleError";
import { BeaconService } from "../service";
import { InstitutionService } from "../../institutions/service";
import { IInstitution } from "../../../shared/interfaces/institution/IInstitution";
import AsyncSelect2 from "../../../shared/components/AsyncSelect2";
import { ISelectOption } from "../../../shared/interfaces/shared/ISelectOption";

const beaconSchema = yup.object().shape({
  uuid: yup
    .string()
    .required("validation.required")
    .test("is-valid-identifier", "validation.uuidInvalid", (value) => {
      if (!value) return false;

      // MAC address format (with colons)
      const macRegex = /^([0-9a-fA-F]{2}:){5}[0-9a-fA-F]{2}$/;

      return macRegex.test(value);
    })
    .trim(),
  minor: yup
    .string()
    .required("validation.required")
    .matches(/^\d+$/, "validation.numberRequired")
    .trim(),
  major: yup
    .string()
    .required("validation.required")
    .matches(/^\d+$/, "validation.numberRequired")
    .trim(),
  title: yup
    .string()
    .required("validation.required")
    .min(2, "validation.minLength")
    .max(100, "validation.maxLength")
    .trim(),
  description: yup.string().max(500, "validation.maxLength").trim(),
  institutionId: yup
    .number()
    .required("validation.required")
    .typeError("validation.required"),
});

const initialValues: Partial<IBeacon> = {
  uuid: "",
  minor: "",
  major: "",
  title: "",
  description: "",
  institutionId: undefined,
};

const BeaconForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { beaconId } = useParams();
  const [defaultInstitutionOptions, setDefaultInstitutionOptions] = useState<
    ISelectOption<number>[]
  >([]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<IBeacon>({
    resolver: yupResolver(beaconSchema) as unknown as Resolver<IBeacon>,
    defaultValues: async () => {
      if (Boolean(beaconId)) {
        try {
          const response = await BeaconService.getSingle(Number(beaconId));
          return {
            ...response?.data,
          };
        } catch (e) {
          handleError(e);
          return initialValues as IBeacon;
        }
      }
      return initialValues as IBeacon;
    },
  });

  const selectedInstitutionId = watch("institutionId");

  // Load institutions from API with search query
  const loadInstitutions = useCallback(
    async (inputValue: string): Promise<ISelectOption<number>[]> => {
      try {
        const response = await InstitutionService.getAll({
          page: 0,
          size: 50,
          name: inputValue || undefined,
        });
        return (response?.data || [])
          .filter((institution: IInstitution) => institution.id !== undefined)
          .map((institution: IInstitution) => ({
            value: institution.id as number,
            label: institution.name,
          }));
      } catch (error) {
        handleError(error);
        return [];
      }
    },
    [],
  );

  // Load default options on mount
  useEffect(() => {
    loadInstitutions("").then(setDefaultInstitutionOptions);
  }, [loadInstitutions]);

  const onSubmit = async (data: IBeacon) => {
    const isUpdate = !!data?.id;
    const correctService = isUpdate
      ? BeaconService.update
      : BeaconService.create;
    await correctService(data, data?.id)
      .then(() => {
        navigate(-1);
        reset();

        enqueueSnackbar(
          isUpdate
            ? t("toasts.successfullyUpdated", {
                name: data?.title,
              })
            : t("toasts.successfullyCreated", {
                name: data?.title,
              }),
          {
            variant: "success",
          },
        );
      })
      .catch(handleError);
  };

  const generateUUID = () => {
    const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
    setValue("uuid", uuid);
  };

  return (
    <React.Fragment>
      <BreadcrumbItem
        mainTitle={t("beacon.beacons")}
        subTitle={
          beaconId ? t("beacon.updateBeacon") : t("beacon.createBeacon")
        }
      />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col md={8}>
            <Card>
              <Card.Header>
                <h5>{t("beacon.beaconInformation")}</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="title">
                      <Form.Label>{t("beacon.title")}</Form.Label>
                      <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="text"
                            placeholder={t("beacon.titlePlaceholder")}
                            isInvalid={!!errors.title?.message}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.title?.message &&
                          t(errors.title.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="institutionId">
                      <Form.Label>{t("general.institution")}</Form.Label>
                      <AsyncSelect2<number>
                        name="institutionId"
                        placeholder={t("general.selectInstitution")}
                        loadOptions={loadInstitutions}
                        defaultOptions={defaultInstitutionOptions}
                        onChange={(value) => {
                          setValue("institutionId", value as number);
                        }}
                        value={selectedInstitutionId}
                      />
                      {errors.institutionId?.message && (
                        <div className="invalid-feedback d-block">
                          {t(errors.institutionId.message as string)}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3" controlId="uuid">
                      <Form.Label>{t("beacon.uuid")}</Form.Label>
                      <div className="input-group">
                        <Controller
                          name="uuid"
                          control={control}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              type="text"
                              placeholder={t("beacon.uuidPlaceholder")}
                              isInvalid={!!errors.uuid?.message}
                            />
                          )}
                        />
                      </div>
                      <Form.Control.Feedback
                        type="invalid"
                        className={errors.uuid?.message ? "d-block" : ""}
                      >
                        {errors.uuid?.message &&
                          t(errors.uuid.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group className="mb-3" controlId="minor">
                      <Form.Label>{t("beacon.minor")}</Form.Label>
                      <Controller
                        name="minor"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="text"
                            placeholder={t("beacon.minorPlaceholder")}
                            isInvalid={!!errors.minor?.message}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.minor?.message &&
                          t(errors.minor.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3" controlId="major">
                      <Form.Label>{t("beacon.major")}</Form.Label>
                      <Controller
                        name="major"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="text"
                            placeholder={t("beacon.majorPlaceholder")}
                            isInvalid={!!errors.major?.message}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.major?.message &&
                          t(errors.major.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3" controlId="description">
                      <Form.Label>{t("beacon.description")}</Form.Label>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            as="textarea"
                            rows={3}
                            placeholder={t("beacon.descriptionPlaceholder")}
                            isInvalid={!!errors.description?.message}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.description?.message &&
                          t(errors.description.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer className="text-end">
                <Button
                  variant="light"
                  className="me-2"
                  onClick={() => navigate(-1)}
                >
                  {t("button.cancel")}
                </Button>
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? t("button.submitting") : t("button.save")}
                </Button>
              </Card.Footer>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <Card.Header>
                <h5>{t("beacon.beaconPreview")}</h5>
              </Card.Header>
              <Card.Body className="text-center">
                <div
                  className="avtar avtar-xl bg-light-primary mb-3 mx-auto"
                  style={{ width: "80px", height: "80px" }}
                >
                  <i className="ph-duotone ph-broadcast f-36"></i>
                </div>
                <h5>{watch("title") || t("beacon.beaconTitle")}</h5>
                <p className="text-muted mb-2">
                  <small>
                    {t("beacon.uuidLabel")}:{" "}
                    {watch("uuid") || t("general.notAvailable")}
                  </small>
                </p>
                <div className="d-flex justify-content-center gap-3 mb-2">
                  <span className="badge bg-primary">
                    {t("beacon.majorLabel")}:{" "}
                    {watch("major") || t("general.notAvailable")}
                  </span>
                  <span className="badge bg-secondary">
                    {t("beacon.minorLabel")}:{" "}
                    {watch("minor") || t("general.notAvailable")}
                  </span>
                </div>
                <p className="text-muted small">
                  {watch("description") || t("beacon.noDescription")}
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </React.Fragment>
  );
};

export default BeaconForm;
